import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiFormHelperText from '@mui/material/FormHelperText';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { mergeSlotProps } from '@mui/material/utils';
import * as React from 'react';
import { Box, FlexBox } from '../box';
import { Button } from '../button';
import { useId } from '../hooks';
import { getIcon, IconArrowDropDown } from '../icon';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { fieldErrorProps } from '../textfield/utils';
import { getPaletteColor } from '../theme';
import { sentence, Typography } from '../typography';
import { withVersion } from '../utils';
const Root = styled(Box)({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
});
const StyledButton = styled(Button)(({ theme, $hasError, $open, $variant = 'primary', $visualState = 'default' }) => {
    const isPrimary = $variant === 'primary';
    const labelDefault = getPaletteColor(theme, `components.simple-select.${$variant}.closed.label-default`);
    const labelActive = getPaletteColor(theme, `components.simple-select.${$variant}.opened.label-active`);
    const borderColor = getPaletteColor(theme, `components.simple-select.${$variant}.border-active`);
    const closedBackground = getPaletteColor(theme, `components.simple-select.${$variant}.closed.bg`);
    const closedHoverBackground = getPaletteColor(theme, `components.simple-select.${$variant}.closed.bg-hover`);
    const closedRippleBackground = getPaletteColor(theme, `components.simple-select.${$variant}.closed.bg-ripple`);
    const openedBackground = getPaletteColor(theme, `components.simple-select.${$variant}.opened.bg`);
    const openedHoverBackground = getPaletteColor(theme, `components.simple-select.${$variant}.opened.bg-hover`);
    const isClosedDefault = !$open && $visualState === 'default';
    const textColor = isPrimary && !isClosedDefault ? labelActive : labelDefault;
    const isHover = $visualState === 'hover';
    const isFocused = $visualState === 'focused';
    const isActive = $visualState === 'active';
    const hoverBackground = $open
        ? openedHoverBackground
        : closedHoverBackground;
    const focusBackground = $open ? openedBackground : closedRippleBackground;
    const resolvedBackgroundColor = (() => {
        if ($open) {
            return isHover ? openedHoverBackground : openedBackground;
        }
        if (isHover) {
            return closedHoverBackground;
        }
        if (isFocused) {
            return closedRippleBackground;
        }
        if (isActive) {
            return isPrimary ? openedBackground : closedBackground;
        }
        return closedBackground;
    })();
    const showBorder = $open || $hasError;
    const borderShadow = `inset 0 0 0 1px ${$hasError ? theme.palette.error.main : borderColor}`;
    return {
        minWidth: 0,
        height: 30,
        boxSizing: 'border-box',
        paddingTop: theme.spacing(0.5),
        paddingRight: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.5),
        paddingLeft: theme.spacing(0.75),
        borderRadius: theme.shape.borderRadius,
        border: 'none',
        boxShadow: showBorder ? borderShadow : 'none',
        backgroundColor: resolvedBackgroundColor,
        color: textColor,
        lineHeight: '22px',
        letterSpacing: '0.46px',
        '&:hover': {
            boxShadow: showBorder ? borderShadow : 'none',
            backgroundColor: hoverBackground,
            color: isPrimary ? labelActive : textColor
        },
        '&.Mui-focusVisible': {
            boxShadow: showBorder ? borderShadow : 'none',
            backgroundColor: focusBackground
        },
        '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            border: 'none',
            boxShadow: 'none',
            backgroundColor: 'transparent'
        }
    };
});
const ButtonText = styled(Typography)(({ theme }) => {
    const labelFontSize = theme.palette.components?.['simple-select']?.label;
    return {
        ...(labelFontSize ? { fontSize: Number(labelFontSize) } : {}),
        lineHeight: 1.2
    };
});
const OpenStateIcon = styled(IconArrowDropDown)(({ $open }) => ({
    color: 'inherit',
    fontSize: 'inherit',
    ...($open && { transform: 'rotate(180deg)' })
}));
const StartAdornment = styled(FlexBox)({
    flexShrink: 0,
    fontSize: 20,
    '& svg': {
        fontSize: 'inherit'
    }
});
const OverlaySkeleton = styled(Skeleton)({
    position: 'absolute'
});
const HelperText = styled(MuiFormHelperText)({
    width: 'fit-content'
});
const StyledMenuItem = styled(MuiMenuItem)(({ theme }) => ({
    minHeight: 36,
    paddingTop: theme.spacing(0.75),
    paddingRight: theme.spacing(1.5),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.5)
}));
const getFallbackOptionKey = (option, index) => {
    if (typeof option === 'string' || typeof option === 'number') {
        return String(option);
    }
    if (typeof option === 'boolean') {
        return option ? 'true' : 'false';
    }
    return String(index);
};
const getButtonLabel = (props) => {
    const { getOptionLabel, hasValue, label, placeholder, value } = props;
    if (hasValue) {
        return sentence(getOptionLabel?.(value) ?? String(value));
    }
    if (label) {
        return label;
    }
    if (placeholder) {
        return sentence(placeholder);
    }
    return '';
};
const getOptionText = (option, getOptionLabel) => {
    return sentence(getOptionLabel?.(option) ?? String(option));
};
const SimpleSelectImpl = React.forwardRef(function SimpleSelectImpl(props, ref) {
    const { buttonProps, className, 'data-role': dataRole, 'data-role-value': dataRoleValue, disabled, error, forceOpen = false, getOptionIcon, getOptionKey, getOptionLabel, helperText, label, menuProps, noOptionsText, options, placeholder, startIcon, skeleton, value, variant = 'primary', visualState = 'default', onChange } = props;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const buttonId = useId();
    const menuId = useId();
    const hasValue = value !== null && value !== undefined;
    const open = forceOpen || Boolean(anchorEl);
    const { error: hasError, helperText: fieldHelperText } = fieldErrorProps(error, helperText);
    const buttonLabel = getButtonLabel({
        getOptionLabel,
        hasValue,
        label,
        placeholder,
        value
    });
    const resolvedStartIcon = variant === 'primary' ? startIcon : undefined;
    const StartIcon = resolvedStartIcon
        ? getIcon(resolvedStartIcon)
        : undefined;
    const closeMenu = () => setAnchorEl(null);
    const onToggle = (event) => {
        setAnchorEl((currentAnchor) => currentAnchor ? null : event.currentTarget);
    };
    const button = (_jsx(StyledButton, { ref: ref, id: buttonId, className: className, "data-role": dataRole ?? 'simple-select', "data-role-value": dataRoleValue, variant: "outlined", color: "inherit", size: buttonProps?.size ?? 'small', disabled: disabled || skeleton, onClick: onToggle, "aria-haspopup": "menu", "aria-expanded": open ? 'true' : undefined, "aria-controls": open ? menuId : undefined, "$hasError": hasError, "$open": open, "$variant": variant, "$visualState": visualState, ...buttonProps, children: _jsxs(FlexBox, { gap: 0.5, vAlign: "center", minWidth: 0, children: [StartIcon && (_jsx(StartAdornment, { vAlign: "center", children: _jsx(StartIcon, { color: "inherit", fontSize: "inherit" }) })), _jsx(ButtonText, { variant: "body2", color: "inherit", fontWeight: "inherit", noWrap: true, children: buttonLabel }), variant === 'primary' && (_jsx(FlexBox, { vAlign: "center", flexShrink: 0, fontSize: 20, children: _jsx(OpenStateIcon, { "$open": open }) }))] }) }));
    return (_jsxs(Root, { children: [skeleton ? (_jsxs(Box, { position: "relative", width: "fit-content", children: [_jsx(OverlaySkeleton, { width: "100%", height: "100%" }), _jsx(Box, { visibility: "hidden", children: button })] })) : (button), _jsxs(MuiMenu, { id: menuId, anchorEl: anchorEl, open: Boolean(anchorEl), onClose: closeMenu, anchorOrigin: menuProps?.anchorOrigin ?? {
                    vertical: 'bottom',
                    horizontal: 'left'
                }, transformOrigin: menuProps?.transformOrigin ?? {
                    vertical: 'top',
                    horizontal: 'left'
                }, slotProps: {
                    ...menuProps?.slotProps,
                    paper: mergeSlotProps(menuProps?.slotProps?.paper, {
                        sx: {
                            minWidth: 'max-content'
                        }
                    }),
                    list: mergeSlotProps(menuProps?.slotProps?.list, {
                        sx: {
                            padding: 0
                        }
                    })
                }, disableAutoFocus: menuProps?.disableAutoFocus, disableEnforceFocus: menuProps?.disableEnforceFocus, disableRestoreFocus: menuProps?.disableRestoreFocus, children: [options.length === 0 && (_jsx(StyledMenuItem, { disabled: true, dense: true, children: noOptionsText })), options.map((option, index) => {
                        const optionKey = getOptionKey
                            ? getOptionKey(option)
                            : getFallbackOptionKey(option, index);
                        const selected = (() => {
                            if (!hasValue) {
                                return false;
                            }
                            if (getOptionKey) {
                                return getOptionKey(value) === optionKey;
                            }
                            return getFallbackOptionKey(value, index) === option;
                        })();
                        const optionIcon = getOptionIcon?.(option);
                        const Icon = optionIcon
                            ? getIcon(optionIcon.icon)
                            : undefined;
                        return (_jsx(StyledMenuItem, { dense: true, selected: selected, "data-role": "simple-select-option", "data-role-value": String(optionKey), onClick: (event) => {
                                closeMenu();
                                onChange?.(option, event, 'selectOption');
                            }, children: _jsxs(FlexBox, { gap: 1, vAlign: "center", children: [Icon && (_jsx(Icon, { ...(optionIcon?.iconProps ?? {}) })), _jsx(Typography, { children: getOptionText(option, getOptionLabel) })] }) }, optionKey));
                    })] }), fieldHelperText && (_jsx(HelperText, { error: hasError, children: fieldHelperText }))] }));
});
export const SimpleSelect = withVersion(SimpleSelectImpl, '1.0.0');
